import { Chart as ChartJS, ArcElement, Legend, Tooltip } from "chart.js";
import type { Chart, TooltipModel } from "chart.js";
import { externalTooltipHandler } from "utils/chart";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import useCoreStore from "views/hooks/useCoreStore";
import useDashboardApi from "../hooks/useDashboardApi";
import { DATE_FORMAT_API } from "config/settings";
import { useTranslation } from "react-i18next";
import { Card, Col, Row, Select, Empty, Spin } from "antd";
import { useParams } from "react-router-dom";
import { Doughnut } from "react-chartjs-2";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FaChartPie } from "react-icons/fa";
import CardTitle from "components/CardTitle";

ChartJS.register(ArcElement, Tooltip, Legend);

// Custom HTML legend plugin
const htmlLegendPlugin = {
  id: "htmlLegend",
  afterUpdate(
    chart: Chart<"doughnut">,
    _args: unknown,
    options: { containerID: string; currencyCode?: string; decimalPlaces?: number },
  ) {
    const legendContainer = document.getElementById(options.containerID);

    // Clear existing legend items
    if (legendContainer) {
      legendContainer.innerHTML = "";
    } else {
      return;
    }

    // Create legend items
    const ul = document.createElement("ul");

    chart.data.datasets[0].data.forEach((item: number, i: number) => {
      if (!chart.getDataVisibility(i)) return;

      const li = document.createElement("li");
      li.style.cursor = "pointer";
      li.onclick = () => {
        chart.toggleDataVisibility(i);
        chart.update();
      };

      // Create label with circular color indicator
      const labelContainer = document.createElement("p");

      const colorBox = document.createElement("span");
      const backgroundColors = chart.data.datasets[0].backgroundColor;
      colorBox.style.background = Array.isArray(backgroundColors)
        ? String(backgroundColors[i])
        : String(backgroundColors ?? "");
      colorBox.style.borderRadius = "50%";
      colorBox.style.display = "inline-block";
      colorBox.style.height = "14px";
      colorBox.style.width = "14px";
      colorBox.style.marginRight = "10px";
      colorBox.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";

      const text = document.createTextNode(String(chart.data.labels?.[i] ?? "").toUpperCase());

      labelContainer.appendChild(colorBox);
      labelContainer.appendChild(text);

      // Create amount element
      const amountElement = document.createElement("strong");
      const currencyCode = options.currencyCode || "";
      const decimalPlaces = options.decimalPlaces || 2;
      const amount = formatNumberWithSpaces(item.toFixed(decimalPlaces)) + " " + currencyCode;
      amountElement.textContent = amount;
      amountElement.style.paddingLeft = "24px";

      li.appendChild(labelContainer);
      li.appendChild(amountElement);
      ul.appendChild(li);
    });

    legendContainer.appendChild(ul);
  },
};

interface PieChartPropsType {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

export default function PieChart(props: PieChartPropsType) {
  const { startDate, endDate } = props;
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const [chartHeight, setChartHeight] = useState(300);

  const currency = useCoreStore(state => state.currency);
  const currencyCode = currency?.code;
  const decimalPlaces = currency?.decimalPlaces;

  const {
    listTopClients,
    refetchListTopClients: refetch,
    topClientsData: data,
    loadingListTopClients: loading,
  } = useDashboardApi();

  useEffect(() => {
    if (urlParams?.companyId) {
      listTopClients({
        variables: {
          filter: {
            companyId: urlParams?.companyId,
            endDate: endDate && dayjs(endDate).format(DATE_FORMAT_API),
            startDate: startDate && dayjs(startDate).format(DATE_FORMAT_API),
            numberOfClients: 5,
          },
        },
      });
    }

    const updateChartHeight = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setChartHeight(250);
      } else {
        setChartHeight(300);
      }
    };

    updateChartHeight();
    window.addEventListener("resize", updateChartHeight);

    return () => window.removeEventListener("resize", updateChartHeight);
  }, [urlParams?.companyId, startDate, endDate]);

  const options = {
    plugins: {
      htmlLegend: {
        containerID: "legend-container",
        currencyCode: currencyCode,
        decimalPlaces: decimalPlaces,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context: { chart: Chart; tooltip: TooltipModel<"doughnut"> }) =>
          externalTooltipHandler(context, String(currencyCode ?? "")),
      },
    },
    maintainAspectRatio: false,
    cutout: "70%",
    radius: "90%",
    animation: {
      animateScale: true,
      animateRotate: true,
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: "#fff",
        borderRadius: 4,
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10,
      },
    },
  };

  const handleOnChange = (value: number) => {
    refetch({
      filter: {
        companyId: urlParams?.companyId,
        endDate: endDate && dayjs(endDate).format(DATE_FORMAT_API),
        startDate: startDate && dayjs(startDate).format(DATE_FORMAT_API),
        numberOfClients: value,
      },
    });
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="chart-loading-container">
          <Spin size="large" />
        </div>
      );
    }

    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="chart-empty-container">
          <Empty description={t("No data available")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return (
      <Row className="pie-chart-content">
        <Col xs={24} sm={24} md={16} className="pie-chart-container">
          <Doughnut
            plugins={[htmlLegendPlugin]}
            width={300}
            height={chartHeight}
            data={data}
            options={options}
          />
        </Col>
        <Col xs={24} sm={24} md={8} className="pie-chart-legend-container">
          <div id="legend-container" className="chart-legend"></div>
        </Col>
      </Row>
    );
  };

  return (
    <Col md={12} sm={24} xs={24}>
      <Card
        className="antd-newrai-card pie-chart-card"
        title={
          <CardTitle icon={<FaChartPie />} title={t("Top customers (turnover excluding tax)")} />
        }
        extra={
          <div className="pie-chart-controls">
            <Select
              className="client-count-select"
              onChange={handleOnChange}
              defaultValue={5}
              options={[
                { label: "5", value: 5 },
                { label: "10", value: 10 },
                { label: "20", value: 20 },
              ]}
              dropdownMatchSelectWidth={false}
            />
          </div>
        }
      >
        {renderChart()}
      </Card>
    </Col>
  );
}
